import { useEffect, useState } from "react";
import { ColumnSort } from "../components/ui/table";
import { filterArrayByValue } from "../lib/filter";
import lodash from 'lodash'

type Props<T, K extends keyof T = keyof T> = {
    data: T[];
    sorting?: ColumnSort;
    filter?: string;
    keysToFilterOn?: K[];
};

// Helper function to access nested values and arrays (e.g., "metadata[].EmailTo")
const getArrayNestedValue = (obj: any, path: string) => {
    const [arrayKey, searchKey] = path.split('[]');
    const [_, searchValue] = searchKey.split('.');

    if (Array.isArray(obj[arrayKey])) {
        // Find the element in the array where the metatype matches searchValue
        const item = obj[arrayKey].find((el: any) => el.metatype === searchValue);
        return item ? (item.metavalue || item.metadata) : undefined;
    }

    return undefined;
};

function useTable<T> (options: Props<T>) {
    const [data, setData] = useState(options.data);
    const [sorting, setSorting] = useState(options.sorting);
    const [filter, setFilter] = useState(options.filter);

    useEffect(() => {
        // Create a copy of the initial data to avoid modifying the original array
        let newData = [...options.data];

        // Sort the data based on the sorting state
        if (sorting) {
            const { id, desc } = sorting;

            newData.sort((a, b) => {
                let aValue, bValue;

                // Check if the id contains "[]", which indicates we're dealing with an array
                if (id.includes('[]')) {
                    aValue = getArrayNestedValue(a, id as string);
                    bValue = getArrayNestedValue(b, id as string);

                } else {
                    aValue = lodash.get(a, id as string);
                    bValue = lodash.get(b, id as string);
                }

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    return desc
                        ? bValue.localeCompare(aValue)
                        : aValue.localeCompare(bValue);
                } else if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return desc ? bValue - aValue : aValue - bValue;
                }
                return 0;
            });

            // Apply filter
            newData = filterArrayByValue(newData as object[], filter || '', options.keysToFilterOn ? (options.keysToFilterOn as string[]) : undefined) as T[];

            // Combine and remove duplicates
            let combineAndRemoveDups = lodash.union(newData);

            setData(combineAndRemoveDups);
        }

    }, [options.data, sorting, filter])

    // Function to update the sorting state
    const updateSorting = (newSorting: ColumnSort) => {
        setSorting(newSorting);
    };

    // Function to update the filter state
    const updateFilter = (newFilter:string) => {
        setFilter(newFilter);
    };


    return { data, sorting, filter, updateSorting, updateFilter };
}


export default useTable;